import { useCallback, useEffect, useState } from "react";
import {
  AdminButton,
  AdminCard,
  AdminErrorBanner,
  AdminInfoRow,
  AdminPageLayout,
  AdminSelect,
  AdminStatusBadge,
  AdminTable,
  AdminTd,
  AdminTextarea,
  AdminTr,
} from "@/components/admin";
import {
  fetchSoundDatasetAudioBlob,
  fetchSoundDatasetSampleDetail,
  fetchSoundDatasetSamples,
  fetchCommonCodes,
  reviewSoundDatasetSample,
  type AdminMutationReasonCode,
  type AdminSoundDatasetSampleDetail,
  type AdminSoundDatasetSampleItem,
  type AdminSoundDatasetReviewFilter,
  type AdminSoundDatasetReviewStatus,
} from "@/lib/api";
import type { CommonCodeOption } from "@/types";
import { formatDateTime } from "@/lib/format";

const filters = [["", "전체"], ["PENDING", "검수 대기"], ["APPROVED", "승인"], ["REJECTED", "반려"], ["TRAINED", "학습 반영"]] as const;

const SoundDatasetSamplesPage = () => {
  const [items, setItems] = useState<AdminSoundDatasetSampleItem[]>([]);
  const [filter, setFilter] = useState<"" | AdminSoundDatasetReviewFilter>("");
  const [tagOptions, setTagOptions] = useState<CommonCodeOption[]>([]);
  const [detail, setDetail] = useState<AdminSoundDatasetSampleDetail | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [loadingAudio, setLoadingAudio] = useState(false);
  const [savingReview, setSavingReview] = useState(false);
  const [reviewStatus, setReviewStatus] = useState<AdminSoundDatasetReviewFilter>("PENDING");
  const [adminTagCode, setAdminTagCode] = useState("");
  const [memo, setMemo] = useState("");
  const [reasonCode, setReasonCode] = useState<AdminMutationReasonCode>("OPERATOR_REQUEST");
  const [reason, setReason] = useState("");
  const [auditMessage, setAuditMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const adminTagCodeIsActive = !adminTagCode || tagOptions.some((option) => option.code === adminTagCode);

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMessage(null);
    try {
      const [samples, soundTagOptions] = await Promise.all([
        fetchSoundDatasetSamples(filter || undefined),
        fetchCommonCodes("SOUND_SUB_TYPE"),
      ]);
      setItems(samples);
      setTagOptions(soundTagOptions);
    } catch (error) {
      setItems([]);
      setErrorMessage(error instanceof Error ? error.message : "데이터셋 목록을 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => () => { if (audioUrl) URL.revokeObjectURL(audioUrl); }, [audioUrl]);

  const openDetail = async (sampleId: number) => {
    setLoadingDetail(true);
    setErrorMessage(null);
    setAudioUrl((previous) => { if (previous) URL.revokeObjectURL(previous); return null; });
    try {
      const selected = await fetchSoundDatasetSampleDetail(sampleId);
      setDetail(selected);
      setReviewStatus(selected.reviewStatus);
      setAdminTagCode(selected.adminTagCode ?? "");
      setMemo(selected.memo ?? "");
      setReason("");
      setAuditMessage(null);
    } catch (error) {
      setDetail(null);
      setErrorMessage(error instanceof Error ? error.message : "데이터셋 상세를 불러오지 못했습니다.");
    } finally {
      setLoadingDetail(false);
    }
  };

  const saveReview = async () => {
    if (!detail || reviewStatus === "TRAINED" || reason.trim().length < 10 || !adminTagCodeIsActive || (reviewStatus === "APPROVED" && !adminTagCode.trim())) return;
    setSavingReview(true);
    setErrorMessage(null);
    try {
      const result = await reviewSoundDatasetSample(detail.soundDatasetSampleId, {
        reviewStatus,
        adminTagCode: adminTagCode.trim() || undefined,
        memo: memo.trim() || undefined,
        reasonCode,
        reason: reason.trim(),
      });
      setDetail(result.sample);
      setReason("");
      setAuditMessage(`검수 결과가 저장되었습니다. 감사 로그 #${result.audit.auditLogId}`);
      await load();
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : "검수 결과를 저장하지 못했습니다.");
    } finally {
      setSavingReview(false);
    }
  };

  const loadAudio = async () => {
    if (!detail) return;
    setLoadingAudio(true);
    setErrorMessage(null);
    try {
      const blob = await fetchSoundDatasetAudioBlob(detail.soundDatasetSampleId);
      setAudioUrl((previous) => { if (previous) URL.revokeObjectURL(previous); return URL.createObjectURL(blob); });
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : "데이터셋 원음을 불러오지 못했습니다.");
    } finally {
      setLoadingAudio(false);
    }
  };

  return (
    <AdminPageLayout
      title="과거 소리 데이터셋"
      subtitle="v1 수집 샘플과 과거 모델 학습 검수 이력을 관리합니다. 최신 EAFR 학습 파이프라인이 아닙니다."
      actions={<AdminButton variant="primary" size="sm" icon={loading ? "ri-loader-4-line animate-spin" : "ri-refresh-line"} onClick={load} disabled={loading}>새로고침</AdminButton>}
    >
      <AdminErrorBanner message={errorMessage} />
      <div className="mt-4 flex flex-wrap gap-2">
        {filters.map(([value, label]) => <AdminButton key={value || "all"} variant={filter === value ? "soft" : "ghost"} size="sm" onClick={() => setFilter(value)}>{label}</AdminButton>)}
      </div>

      <div className="mt-4 grid gap-4 xl:grid-cols-[minmax(0,1.6fr)_minmax(320px,1fr)]">
        <AdminCard className="min-w-0 overflow-hidden">
          <AdminTable headers={["샘플", "수집 태그", "모델", "검수", "학습", "등록 시각", ""]} loading={loading} emptyMessage={!loading && items.length === 0 ? "조건에 맞는 데이터셋 샘플이 없습니다." : undefined}>
            {items.map((item) => (
              <AdminTr key={item.soundDatasetSampleId} className="hover:bg-slate-50">
                <AdminTd><span className="text-xs font-bold text-slate-700">#{item.soundDatasetSampleId}</span></AdminTd>
                <AdminTd><p className="text-xs font-semibold text-slate-700">{item.adminTagName ?? item.userTagName ?? "-"}</p><p className="text-xs text-slate-400">{item.collectorName}</p></AdminTd>
                <AdminTd><span className="text-xs text-slate-600">{item.modelName ?? "-"} {item.modelVersion ?? ""}</span></AdminTd>
                <AdminTd><AdminStatusBadge group="DATASET_REVIEW" code={item.reviewStatus} label={item.reviewStatus} /></AdminTd>
                <AdminTd><span className="text-xs text-slate-600">{item.trainingStatus}</span></AdminTd>
                <AdminTd><span className="whitespace-nowrap text-xs text-slate-600">{formatDateTime(item.registeredAt, 0, 16)}</span></AdminTd>
                <AdminTd><AdminButton variant="secondary" size="sm" onClick={() => openDetail(item.soundDatasetSampleId)}>상세</AdminButton></AdminTd>
              </AdminTr>
            ))}
          </AdminTable>
        </AdminCard>

        <AdminCard className="min-w-0 p-4">
          {loadingDetail && <p className="py-10 text-center text-xs text-slate-400">상세 정보를 불러오는 중입니다.</p>}
          {!loadingDetail && !detail && <p className="py-10 text-center text-sm text-slate-500">샘플을 선택하면 상세와 모델 후보를 확인할 수 있습니다.</p>}
          {!loadingDetail && detail && <div className="space-y-4">
            <div><h3 className="text-sm font-bold text-slate-800">샘플 #{detail.soundDatasetSampleId}</h3><p className="mt-1 text-xs text-slate-500">{detail.file.mimeType} · {detail.file.durationSeconds ?? "-"}초</p></div>
            <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-1 2xl:grid-cols-2">
              <AdminInfoRow dense bordered label="검수 상태" value={detail.reviewStatus} />
              <AdminInfoRow dense bordered label="매핑 상태" value={detail.mappingStatus} />
              <AdminInfoRow dense bordered label="학습 상태" value={detail.trainingStatus} />
              <AdminInfoRow dense bordered label="모델" value={`${detail.modelName ?? "-"} ${detail.modelVersion ?? ""}`} />
              <AdminInfoRow dense bordered label="샘플레이트" value={detail.file.sampleRate ?? "-"} />
              <AdminInfoRow dense bordered label="채널" value={detail.file.channelCount ?? "-"} />
            </div>
            {audioUrl ? <audio className="w-full" controls preload="none" src={audioUrl} /> : <AdminButton variant="primary" icon={loadingAudio ? "ri-loader-4-line animate-spin" : "ri-play-circle-line"} disabled={loadingAudio} onClick={loadAudio}>원음 불러오기</AdminButton>}
            <div><h4 className="mb-2 text-xs font-bold text-slate-700">모델 후보</h4><div className="space-y-2">{detail.yamnetResults.map((result) => <div key={result.soundDatasetYamnetResultId} className="flex items-center justify-between gap-3 rounded-lg border border-slate-100 bg-slate-50 p-2.5"><span className="text-xs text-slate-700">#{result.rankNo} {result.modelLabelKo ?? result.modelLabel}</span><span className="text-xs font-semibold text-blue-700">{Math.round(result.score * 100)}%</span></div>)}{detail.yamnetResults.length === 0 && <p className="text-xs text-slate-400">저장된 모델 후보가 없습니다.</p>}</div></div>
            <div className="space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
              <h4 className="text-xs font-bold text-slate-700">검수 판정</h4>
              <AdminSelect value={reviewStatus} onValueChange={(nextValue) => setReviewStatus(nextValue as AdminSoundDatasetReviewStatus)} disabled={savingReview}>{reviewStatus === "TRAINED" && <option value="TRAINED" disabled>과거 학습 반영 상태 · 새 검수 상태 선택 필요</option>}<option value="PENDING">검수 대기</option><option value="APPROVED">승인</option><option value="REJECTED">반려</option></AdminSelect>
              {reviewStatus === "TRAINED" && <p className="text-xs font-medium text-amber-700">현재 시스템에는 학습 상태를 변경하는 안전한 전이 기능이 없습니다. 새 검수 상태를 선택해야 저장할 수 있습니다.</p>}
              <AdminSelect value={adminTagCode} onValueChange={setAdminTagCode} disabled={savingReview}><option value="">{reviewStatus === "APPROVED" ? "관리자 태그 선택 (승인 시 필수)" : "관리자 태그 없음"}</option>{adminTagCode && !adminTagCodeIsActive && <option value={adminTagCode} disabled>{detail.adminTagName ?? adminTagCode} ({adminTagCode}, 비활성·재선택 필요)</option>}{tagOptions.map((option) => <option key={option.code} value={option.code}>{option.name} ({option.code})</option>)}</AdminSelect>
              {!adminTagCodeIsActive && <p className="text-xs font-medium text-amber-700">기존 태그가 현재 활성 공통코드에 없습니다. 저장하려면 활성 태그를 다시 선택하거나 태그를 비워야 합니다.</p>}
              <AdminTextarea value={memo} onChange={(event) => setMemo(event.target.value)} disabled={savingReview} rows={2} placeholder="검수 메모" />
              <AdminSelect value={reasonCode} onValueChange={(nextValue) => setReasonCode(nextValue as AdminMutationReasonCode)} disabled={savingReview}><option value="OPERATOR_REQUEST">운영 요청</option><option value="DATA_CORRECTION">데이터 교정</option><option value="SECURITY_RESPONSE">보안 대응</option><option value="TEST_ROLLBACK">테스트·원복 검증</option><option value="OTHER">기타</option></AdminSelect>
              <AdminTextarea value={reason} onChange={(event) => setReason(event.target.value)} disabled={savingReview} maxLength={500} rows={3} placeholder="판정 사유를 10자 이상 입력하세요." />
              {auditMessage && <p className="text-xs font-medium text-emerald-700">{auditMessage}</p>}
              <AdminButton variant="primary" size="sm" disabled={savingReview || reviewStatus === "TRAINED" || reason.trim().length < 10 || !adminTagCodeIsActive || (reviewStatus === "APPROVED" && !adminTagCode.trim())} onClick={() => void saveReview()}>{savingReview ? "저장 중..." : "검수 결과 저장"}</AdminButton>
            </div>
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">물리 삭제는 영향 미리보기·대상 재확인·파일 삭제 보상·감사 이력이 모두 적용된 command API 제공 전까지 실행할 수 없습니다.</div>
          </div>}
        </AdminCard>
      </div>
    </AdminPageLayout>
  );
};

export default SoundDatasetSamplesPage;
